import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationButtons from '../../../components/Common/Pagination';
import { AdminSearchInput, TableInfoBar } from '../../../components/Admin';
import { BookGenerationRow, BookGenerationStatus } from '../../../interfaces/bookGeneration.interface';
import {
  fetchBookGenerations,
  fetchQueueTimeStats,
  updateBookPageTotal,
  BookGenerationsFilters,
  QueueTimeStats,
} from './services/BookGenerations.service';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import BookGenerationsTable from './components/BookGenerationsTable';
import BookErrorsModal from './components/BookErrorsModal';
import { TokenCookieManager } from '../../../utils/tokenCookies';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'generating', label: 'Generating' },
  { value: 'complete', label: 'Complete' },
  { value: 'error', label: 'Error' },
  { value: 'story_ready', label: 'Story Ready' },
  { value: 'draft', label: 'Draft' },
];

const BookGenerations = () => {
  const [loader, setLoader] = useState(true);
  const [errorModalBookId, setErrorModalBookId] = useState<string | null>(null);
  const [books, setBooks] = useState<BookGenerationRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hasErrorsOnly, setHasErrorsOnly] = useState(false);
  const [queueStats, setQueueStats] = useState<QueueTimeStats | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages!) setCurrentPage(+page);
  };

  const buildFilters = useCallback((): BookGenerationsFilters => {
    const filters: BookGenerationsFilters = {};
    if (statusFilter) filters.status = statusFilter as BookGenerationStatus;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    if (hasErrorsOnly) filters.hasErrorsOnly = true;
    return filters;
  }, [statusFilter, dateFrom, dateTo, hasErrorsOnly]);

  const handleFetch = async (search?: string) => {
    try {
      setLoader(true);
      const { books: data, totalPages: tp, totalRecords: tr } = await fetchBookGenerations(currentPage, buildFilters(), search ?? (searchTerm || undefined));
      setBooks(data);
      setTotalPages(tp);
      setTotalRecords(tr);
    } finally {
      setLoader(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    handleFetch(term || undefined);
  };

  // Fetch on mount + page change
  useEffect(() => {
    handleFetch(searchTerm || undefined);
    fetchQueueTimeStats(24).then(setQueueStats);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      handleFetch(searchTerm || undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [statusFilter, dateFrom, dateTo, hasErrorsOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdatePageTotal = async (bookId: string, pageTotal: number) => {
    await updateBookPageTotal(bookId, pageTotal);
    // Optimistically update local row so the table reflects the new value immediately
    setBooks((prev) =>
      prev.map((b) => (b.bookId === bookId ? { ...b, pageTotal } : b))
    );
  };

  return (
    <div className="p-0 md:p-4">
      {/* ── Queue Stats Banner ────────────────────────────── */}
      {queueStats && queueStats.sampleCount > 0 && (
        <div className="flex flex-wrap gap-4 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-600 font-medium">Avg Queue Time (24h):</span>
            <span className="text-sm font-semibold text-blue-800">
              {queueStats.avgQueueTimeMs < 1000
                ? `${queueStats.avgQueueTimeMs}ms`
                : `${(queueStats.avgQueueTimeMs / 1000).toFixed(1)}s`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-600 font-medium">Max Queue Time:</span>
            <span className="text-sm font-semibold text-blue-800">
              {queueStats.maxQueueTimeMs < 1000
                ? `${queueStats.maxQueueTimeMs}ms`
                : `${(queueStats.maxQueueTimeMs / 1000).toFixed(1)}s`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-600 font-medium">Jobs Sampled:</span>
            <span className="text-sm font-semibold text-blue-800">{queueStats.sampleCount}</span>
          </div>
        </div>
      )}

      {/* ── Filter Bar ─────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-end mb-4">
        {/* Search (left) */}
        <AdminSearchInput
          placeholder="Search by Book ID or Email"
          onSearch={handleSearch}
          debounceMs={300}
          minChars={3}
          className="w-64"
        />

        {/* Status */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 font-semibold mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date From */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 font-semibold mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </div>

        {/* Date To */}
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 font-semibold mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          />
        </div>

        {/* Has Errors Only */}
        <div className="flex items-center gap-1.5 pb-0.5">
          <input
            type="checkbox"
            id="hasErrorsOnly"
            checked={hasErrorsOnly}
            onChange={(e) => setHasErrorsOnly(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="hasErrorsOnly" className="text-sm text-gray-600 cursor-pointer">
            Errors only
          </label>
        </div>

        {/* Refresh (right-aligned) */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => handleFetch(searchTerm || undefined)}
            disabled={loader}
            className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded px-3 py-1.5 border border-gray-300 disabled:opacity-50 cursor-pointer"
            title="Refresh"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      {loader ? (
        <LoadingSpinner />
      ) : (
        <>
          <BookGenerationsTable
            books={books}
            onViewErrors={(bookId) => setErrorModalBookId(bookId)}
            onUpdatePageTotal={handleUpdatePageTotal}
          />
          <div className="flex justify-between items-center mt-4">
            <TableInfoBar
              currentPage={currentPage}
              pageSize={25}
              totalPages={totalPages || 0}
              totalRecords={totalRecords}
              recordsOnPage={books.length}
            />
            {(totalPages ?? 0) > 1 && (
              <PaginationButtons
                totalPages={totalPages!}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}

      {/* Error Logs Modal */}
      {errorModalBookId && (
        <BookErrorsModal
          bookId={errorModalBookId}
          onClose={() => setErrorModalBookId(null)}
        />
      )}
    </div>
  );
};

export default BookGenerations;
